import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { PageInterface } from 'src/app/workflow/planner/page/page.interface';
import { RecommendationService } from 'src/shared/services/recommendation.service';
import { ShiftRecordStatus } from 'src/shared/models/shift-record-status';
import { AncillaryWorkflowPage } from 'src/shared/models/workflow-page-ancllary.model';
import { WorkflowUtilityService } from 'src/shared/services/workflow-utility.service';
import { debounceTime } from 'rxjs/operators';
import { AncillaryEnterCurrentBaseComponent } from '../../base-classes/ancillary-enter-current-base.component';
import { TotalUOSShiftRecordService } from 'src/shared/services/shift-record-total-uos.service';
import { UnitOfServiceRecord } from 'src/shared/models/unit-of-service-record.model';
import { Constants } from 'src/shared/utilities/constants.util';
import { HoursCalculator } from 'src/shared/models/Hours-calculator.model';
import { StaffRatioValue } from 'src/shared/models/staff-ratio-value.model';
import { ShiftBlock } from 'src/shared/models/shift-block.model';
import { MiscUtil } from 'src/shared/utilities/misc.util';


@Component({
    selector: 'smt-enter-current-uos',
    templateUrl: './enter-current-uos.component.html',
    styleUrls: ['./enter-current-uos.component.scss']
})
export class TotalUOSEnterCurrentComponent extends AncillaryEnterCurrentBaseComponent implements OnInit, PageInterface {
    @Input() page: AncillaryWorkflowPage;
    @Output() previousRequested = new EventEmitter();

    public addOnsValue: any = '';
    public addOnsPercentage = 0;
    public totalUOSForm = new UntypedFormGroup({});
    public totalUOSFormValues$;
    public formGroups: UntypedFormGroup[];
    public totalUos = 0;
    public projectedTotalUosRecord: UnitOfServiceRecord;

    private controlName = 'scheduledUos';
    public showHoursCalculatorModal=false;    
    public staffWarningRows:HoursCalculator[]=[];
    public targetStaffs :StaffRatioValue[];
    public shiftBlock: ShiftBlock;
    loading: boolean;
    
    get unitOfServiceName(): string {
        return this.page.unitOfServiceName;
    }

    constructor(
        protected _recommendationService: RecommendationService,
        protected _router: Router,
        protected _totalUOSShiftRecordService: TotalUOSShiftRecordService,
        protected _workflowUtilityService: WorkflowUtilityService) {
        super(_recommendationService, _router, _workflowUtilityService);
    }

    ngOnInit() {
        super.initialize(this.page, () => this.initialize());
    }

    initialize() {
        this.shiftRecord = this.page.shiftRecord;
        this.shiftBlock = this.shiftRecord.shiftBlock;
        this.projectedTotalUosRecord = this.shiftRecord.unitOfServiceRecords.find(uos => uos.isProjected);
        // calculate the % of AddOns from the Cost Center Config.
        this.addOnsPercentage = this.shiftRecord.shiftBlock.addOns / 100;
        this.initializeTotalUOSForm();
        this.initializeStaffForm();
        this.formGroups = [this.totalUOSForm, this.staffForm];
    }
    
    private async initializeTargetStaff() {
        this.targetStaffs=[];
        this.shiftRecord.costCenter.variableJobTitles.forEach((JobTitle) => {
            let targetstaff: StaffRatioValue = new StaffRatioValue();
            targetstaff.jobTitleId=JobTitle.id;
            targetstaff.staffValue=this.getRecommendedStaffValue(JobTitle.id);
            this.targetStaffs.push(targetstaff);
        })
    }

    private async initializeTotalUOSForm() {
        let scheduledUosValue = '';
        if (this.projectedTotalUosRecord) {
            let scheduleValue: number = this._workflowUtilityService.getSchedulesValue(this.projectedTotalUosRecord.total,this.addOnsPercentage);
            scheduledUosValue = (scheduleValue).toString();
            this.totalUos = this.projectedTotalUosRecord.total;
            this.addOnsValue = this.totalUos - scheduleValue;
            this.updateTotalUosStaffingRecommendation();
        }

        let validators = [Validators.required, Validators.min(this.minValue), Validators.max(this.maxValue), Validators.pattern(Constants.NoDecimalRegex)];
        this.totalUOSForm.addControl(this.controlName, new UntypedFormControl(scheduledUosValue, validators));

        this.totalUOSFormValues$ = this.totalUOSForm.valueChanges.pipe(
            debounceTime(Constants.DebounceTime)
        ).subscribe(async values => {

            let schedulesControl = this.totalUOSForm.get(this.controlName);
            this.calculateAddOnsAndTotalValues(schedulesControl.value);
            this.updateTotalUosStaffingRecommendation();
        });
    }

    valueOutOfRange(): boolean {
        let control = this.totalUOSForm.get(this.controlName);
        return control && !control.valid && (control.touched || control.dirty) && (control.errors.min || control.errors.max || control.errors.pattern);
    }

    controlValueOutOfRange(staffFormId: number): boolean {
        let control = this.staffForm.get(this.getStaffControlName(staffFormId));
        return this._workflowUtilityService.valueOutOfRange(control, this.minValue, this.maxValue);
    }

    isFormValid(): boolean {
        return this.totalUOSForm && this.totalUOSForm.valid && this.staffForm && this.staffForm.valid && this.isValidStaffControls();
    }    

    save(): Promise<number> {
        this.setStaffPlans();
        this.setStatus();
        this.setTotalUOS();

        return this._totalUOSShiftRecordService.setShiftRecordWithAssociated(this.shiftRecord);
    }

    shouldSkipSave(): boolean {
        return false;
    }

    private updateTotalUosStaffingRecommendation() {
        return this.updateStaffingRecommendation(this._recommendationService.makeTotalUosRecommendation(this.shiftRecord.shiftBlockId, this.shiftRecord.workflowTimeFacility, this.totalUos))
    }

    private setStatus() {
        if (this.shiftRecord.status !== ShiftRecordStatus.Done && this.page.nextPage) {
            this.shiftRecord.status = this.page.nextPage.completedStatus;
        }
    }

    private setTotalUOS() {
        if (this.shiftRecord) {
            let control = this.totalUOSForm.get(this.controlName);
            if (!this.projectedTotalUosRecord) {
                this.projectedTotalUosRecord = new UnitOfServiceRecord();
            }
            this.projectedTotalUosRecord.isProjected = true;
            this.projectedTotalUosRecord.shiftBlockId = this.shiftRecord.shiftBlockId;
            this.projectedTotalUosRecord.workflowTimeFacility = this.shiftRecord.workflowTimeFacility;
            this.projectedTotalUosRecord.unitOfServiceId = this.shiftRecord.costCenter.ancillaryConfiguration.unitOfServiceId;
            this.projectedTotalUosRecord.total = +control.value + this.addOnsValue;

            if (!this.shiftRecord.unitOfServiceRecords) {
                this.shiftRecord.unitOfServiceRecords = [];
            }
            this.shiftRecord.unitOfServiceRecords.push(this.projectedTotalUosRecord);
        }
    }

    private calculateAddOnsAndTotalValues(scheduleValue: any) {
        this.addOnsValue = scheduleValue != null ? Math.round(scheduleValue * this.addOnsPercentage) : '';
        this.totalUos = (+scheduleValue) + (+this.addOnsValue);
    }

    openHoursCalculator() {  
        this.initializeTargetStaff();
        this.showHoursCalculatorModal=true;
    }

    ShowHoursWarning(rows)
    {
        this.showHoursCalculatorModal= false
        this.staffWarningRows=rows;
    }

    CloseCalculatorModal(reject)
    {     
        this.showHoursCalculatorModal= false
     }

    getStaffWarning(JobTitleid)
    {
      let row=  this.staffWarningRows.find(a => a.JobTitle.id == JobTitleid);
      let warning:String[]=[];      

        if(row.FourHoursStaff > 0)
           warning.push(` 4 hours - ${row.FourHoursStaff} Staff`) ;

        if(row.EightHoursStaff > 0)
           warning.push(` 8 hours - ${row.EightHoursStaff} Staff`) ;

        if(row.TenHoursStaff > 0)
           warning.push(` 10 hours - ${row.TenHoursStaff} Staff`) ;

        if(row.TwelveHoursStaff > 0)
           warning.push(` 12 hours - ${row.TwelveHoursStaff} Staff`) ;

      return `${row.TotalHours} Total Hours: ` + warning.toString();
    }



    isAddOnsPresent(addOnsValue: number){
        return this._workflowUtilityService.isAddOnsPresent(addOnsValue); 
    }
}
